/**
 * What a goal card shows, decided in one place.
 * Web (dijipo-web-main/…/src/utils/goalCard.ts) and mobile (dijipo-mobile/utils/goalCard.ts)
 * keep byte-for-byte identical copies — change both.
 *
 * Every goal reads the same way: one phase (rail colour + at most one status
 * chip), the count as the main figure, one quiet stat line, and the same
 * actions in the same order. ⚡ marks a goal the LMS counts by itself.
 *
 * Colours come twice: hex for the web's inline styles, and literal Tailwind
 * classes for the mobile app (NativeWind has no style={{}} — and a class must
 * appear verbatim in source to be compiled, so they are spelled out here).
 */

export type GoalPhase = 'upcoming' | 'running' | 'done' | 'missed';
export type GoalTrackingMode = 'lms' | 'manual' | 'both';

export interface GoalCardInput {
  target_value?: number | null;
  achieved_value?: number | null;
  correct_count?: number | null;
  wrong_count?: number | null;
  manual_solved?: number | null;
  auto_tracked?: boolean | null;
  tracking_mode?: string | null;
  is_completed?: boolean | null;
  is_archived?: boolean | null;
  is_upcoming?: boolean | null;
  assignment_id?: string | number | null;
}

export const GOAL_PHASE_STYLE: Record<GoalPhase, {
  color: string; chip: string | null; railClass: string; textClass: string; chipClass: string;
}> = {
  upcoming: { color: '#9aa1ab', chip: 'Başlamadı', railClass: 'bg-[#9aa1ab]', textClass: 'text-[#9aa1ab]', chipClass: 'bg-[#9aa1ab]' },
  running: { color: '#3973bf', chip: null, railClass: 'bg-[#3973bf]', textClass: 'text-[#3973bf]', chipClass: 'bg-[#3973bf]' },
  done: { color: '#2fae4e', chip: 'Tamamlandı', railClass: 'bg-[#2fae4e]', textClass: 'text-[#2fae4e]', chipClass: 'bg-[#2fae4e]' },
  missed: { color: '#e05252', chip: 'Tamamlanmadı', railClass: 'bg-[#e05252]', textClass: 'text-[#e05252]', chipClass: 'bg-[#e05252]' },
};

/** The ⚡ mark's colour. */
export const GOAL_AUTO_COLOR = '#f2a900';

/** Said once above the student's list, next to a ⚡. */
export const GOAL_LIST_NOTE = "işaretli hedefler, eis ekosis LMS'te çözdüğün sorularla otomatik sayılır.";

/** Older goals carry no mode: auto-tracked ones read as both, the rest as manual. */
export function goalTracking(g: GoalCardInput): GoalTrackingMode {
  const m = g.tracking_mode;
  if (m === 'lms' || m === 'manual' || m === 'both') return m;
  return g.auto_tracked ? 'both' : 'manual';
}

/** The LMS counts this goal by itself — show ⚡. */
export const goalCountsLms = (g: GoalCardInput): boolean => goalTracking(g) !== 'manual';
/** The student may add solved questions by hand. */
export const goalAcceptsManual = (g: GoalCardInput): boolean => goalTracking(g) !== 'lms';

export function goalPhase(g: GoalCardInput): GoalPhase {
  const target = g.target_value ?? 0;
  if (g.is_completed || (target > 0 && (g.achieved_value ?? 0) >= target)) return 'done';
  if (g.is_archived) return 'missed';
  if (g.is_upcoming) return 'upcoming';
  return 'running';
}

/** One explanatory line, only where the card would otherwise look broken. */
export function goalHint(g: GoalCardInput, phase: GoalPhase): string | null {
  if (phase !== 'upcoming') return null;
  if (g.assignment_id) return 'Ödevi şimdiden çözebilirsin; çözdüğün sorular bu hedefe sayılır.';
  return goalCountsLms(g)
    ? 'Dönem başlayınca çözdüğün sorular otomatik sayılır.'
    : 'Dönem başlayınca çözdüklerini buradan eklersin.';
}

export function goalActions(g: GoalCardInput): { homework: boolean; addOutside: boolean } {
  const live = !g.is_archived;
  return {
    homework: live && g.assignment_id != null && g.assignment_id !== '',
    // The backend refuses entries dated outside the goal's period.
    addOutside: live && goalAcceptsManual(g) && !g.is_upcoming,
  };
}

/** The add button's wording: "outside" only means something when the LMS also counts. */
export const goalAddLabel = (g: GoalCardInput): string =>
  goalTracking(g) === 'manual' ? '+ Çözdüklerimi ekle' : '+ Dışarıda çözdüklerimi ekle';

export function goalHasStats(g: GoalCardInput): boolean {
  return (g.correct_count ?? 0) + (g.wrong_count ?? 0) > 0 || (g.manual_solved ?? 0) > 0;
}

/** "+N dışarıdan" belongs only to goals that also count LMS work. */
export const goalShowsOutsideCount = (g: GoalCardInput): boolean =>
  goalTracking(g) === 'both' && (g.manual_solved ?? 0) > 0;

// ── Hedef Görünümü lists ─────────────────────────────────────────────────────

export type GoalArchiveRange = 'today' | 'week' | 'month' | 'all';

export const GOAL_ARCHIVE_RANGES: { value: GoalArchiveRange; label: string }[] = [
  { value: 'today', label: 'Bugün' },
  { value: 'week', label: 'Bu hafta' },
  { value: 'month', label: 'Bu ay' },
  { value: 'all', label: 'Tümü' },
];

export const GOAL_ARCHIVE_EMPTY: Record<GoalArchiveRange, string> = {
  today: 'Bugün tamamlanan hedef yok.',
  week: 'Bu hafta arşive giren hedef yok.',
  month: 'Bu ay arşive giren hedef yok.',
  all: 'Arşivde hedef yok.',
};

/** How long a goal stays lit after a tap on it in Program Görünümü. */
export const GOAL_SPOTLIGHT_MS = 2000;

export interface GoalPlaceInput extends GoalCardInput {
  period_start?: string | null;
  period_end?: string | null;
  goal_date?: string | null;
}

export type GoalListPlace = { tab: 'aktif' } | { tab: 'arsiv'; range: GoalArchiveRange };

const isoAt = (y: number, m: number, d: number) => new Date(Date.UTC(y, m, d)).toISOString().slice(0, 10);

/**
 * Where Hedef Görünümü lists a goal, so a tap in Program Görünümü can open it:
 * Aktif, or the narrowest Arşiv range that holds it. The windows are the API's
 * archiveRangeWindow (birey-backend/src/modules/student/goal-progress.ts):
 * a goal is in a range when its period touches that day / week / month.
 */
export function goalListPlace(g: GoalPlaceInput, todayIso: string): GoalListPlace {
  if (!g.is_archived) return { tab: 'aktif' };
  const s = String(g.period_start ?? g.goal_date ?? '').slice(0, 10);
  const e = String(g.period_end ?? g.goal_date ?? '').slice(0, 10);
  if (!s || !e) return { tab: 'arsiv', range: 'all' };
  const y = Number(todayIso.slice(0, 4));
  const m = Number(todayIso.slice(5, 7)) - 1;
  const d = Number(todayIso.slice(8, 10));
  const sinceMonday = (new Date(Date.UTC(y, m, d)).getUTCDay() + 6) % 7;
  const windows: Array<[GoalArchiveRange, string, string]> = [
    ['today', todayIso, todayIso],
    ['week', isoAt(y, m, d - sinceMonday), isoAt(y, m, d - sinceMonday + 6)],
    ['month', isoAt(y, m, 1), isoAt(y, m + 1, 0)],
  ];
  for (const [range, from, to] of windows) {
    if (s <= to && e >= from) return { tab: 'arsiv', range };
  }
  return { tab: 'arsiv', range: 'all' };
}

export const GOAL_ACTIVE_EMPTY: Record<GoalArchiveRange, string> = {
  today: 'Bugün için aktif hedef yok.',
  week: 'Bu hafta için aktif hedef yok.',
  month: 'Bu ay için aktif hedef yok.',
  all: 'Aktif hedef yok.',
};

/** The calendar window of a range on the school day `todayIso`; null = no bound. */
export function goalRangeWindow(range: GoalArchiveRange, todayIso: string): { from: string; to: string } | null {
  if (range === 'all') return null;
  const y = Number(todayIso.slice(0, 4));
  const m = Number(todayIso.slice(5, 7)) - 1;
  const d = Number(todayIso.slice(8, 10));
  if (range === 'today') return { from: todayIso, to: todayIso };
  if (range === 'week') {
    const sinceMonday = (new Date(Date.UTC(y, m, d)).getUTCDay() + 6) % 7;
    return { from: isoAt(y, m, d - sinceMonday), to: isoAt(y, m, d - sinceMonday + 6) };
  }
  return { from: isoAt(y, m, 1), to: isoAt(y, m + 1, 0) };
}

/** A goal is in a range when its period touches that day / week / month. */
export function goalInRange(g: GoalPlaceInput, range: GoalArchiveRange, todayIso: string): boolean {
  const win = goalRangeWindow(range, todayIso);
  if (!win) return true;
  const s = String(g.period_start ?? g.goal_date ?? '').slice(0, 10);
  const e = String(g.period_end ?? g.goal_date ?? '').slice(0, 10);
  if (!s || !e) return true;
  return s <= win.to && e >= win.from;
}
